import { useEffect, useMemo, useState, useCallback } from 'react';
import keyBy from 'lodash/keyBy';
import { Box } from '@mui/material';
import { useTeamsPendingDeletionQuery } from 'TeamModule/query/hooks/useTeamsPendingDeletionQuery';
import StandardPageHeader from 'components/StandardPageHeader';
import { useUpdateTeamApproverMutation } from 'TeamModule/query/hooks/useUpdateTeamApproverMutation';
import { useRevertTeamApprovalMutation } from 'TeamModule/query/hooks/useRevertTeamApprovalMutation';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'selectors';
import useUrl from 'hooks/useUrl';
import { DataDeletionReviewTable } from './DataDeletionReviewTable/DataDeletionReviewTable';
import { useTablePagination } from 'hooks/useTablePagination';
import { ApprovalPersonnel } from 'TeamModule/constants/approval';
import { ORDER_DIRECTION } from '@constants';
import { TeamSearchSortOrder } from 'TeamModule/api/searchTeamsByFieldsApi';
import { COLUMNS, SORT_BY_MAP } from './DataDeletionReviewTable/columns';

export const DataDeletionReviewPage = () => {
  const { queryParams, pushQueryParams } = useUrl();
  const pageFromQueryStr = queryParams.get('page') || '';
  const initialPage = Math.max(1, Number(pageFromQueryStr || 1) || 1);
  const { pageNumber, handlePageChange, limit } = useTablePagination(20, initialPage, 1);
  // Sorting state for UI (column ids) and direction
  const [orderBy, setOrderBy] = useState<string>(COLUMNS.DELETION_DATE);
  const [orderDirection, setOrderDirection] = useState<
    (typeof ORDER_DIRECTION)[keyof typeof ORDER_DIRECTION]
  >(ORDER_DIRECTION.DESC);
  const [selectedIdsState, setSelectedIdsState] = useState<Set<string>>(new Set());
  // Map UI column id -> API sortBy value
  const apiSortBy = useMemo(() => SORT_BY_MAP[orderBy], [orderBy]);

  const sortOrderParam: TeamSearchSortOrder =
    orderDirection === ORDER_DIRECTION.ASC ? 'ASC' : 'DESC';
  const { teamsPendingDeletion, isLoading } = useTeamsPendingDeletionQuery({
    page: pageNumber,
    limit,
    ...(apiSortBy ? { sortBy: apiSortBy } : {}),
    sortOrder: sortOrderParam,
  });

  const teams = useMemo(
    () => (Array.isArray(teamsPendingDeletion?.data) ? teamsPendingDeletion.data : []),
    [teamsPendingDeletion],
  );
  const total = teamsPendingDeletion?.total || 0;

  const allIdsOnPage = useMemo(() => new Set(teams.map((t) => t.id)), [teams]);
  const selectedIds = useMemo(() => new Set(selectedIdsState), [selectedIdsState]);
  const teamById = useMemo(() => keyBy(teams, 'id'), [teams]);
  const selectedCountOnPage = useMemo(
    () => Array.from(allIdsOnPage).filter((id) => selectedIds.has(id)).length,
    [allIdsOnPage, selectedIds],
  );
  const isAllSelectedOnPage = allIdsOnPage.size > 0 && selectedCountOnPage === allIdsOnPage.size;
  const isIndeterminateOnPage = selectedCountOnPage > 0 && selectedCountOnPage < allIdsOnPage.size;
  const hasSelectionOnPage = selectedCountOnPage > 0;

  const toggleSelectAllOnPage = (checked: boolean) => {
    setSelectedIdsState((prev) => {
      const next = new Set(prev);
      if (checked) {
        allIdsOnPage.forEach((id) => next.add(id));
      } else {
        allIdsOnPage.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const toggleRow = (rowId: string) => {
    setSelectedIdsState((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const authUser = useSelector(selectAuthUser);
  const approverName = authUser?.account?.name || '';
  const { updateTeamApprover, isUpdatingTeamApprover } = useUpdateTeamApproverMutation();
  const { revertTeamApproval, isRevertingTeamApproval } = useRevertTeamApprovalMutation();

  const handleBulkApprove = (type: ApprovalPersonnel) => {
    // Approve all selected rows; derive tenant/realmId from current data
    selectedIds.forEach((rowId) => {
      const team = teamById[rowId];
      if (!team) return;
      updateTeamApprover({
        teamId: team.teamId,
        ...(type === ApprovalPersonnel.Developer
          ? { developerApprover: approverName }
          : { supportApprover: approverName }),
        tenant: team.tenant,
        realmId: team.realmId,
      });
    });
  };

  const handleBulkUnapprove = (type: ApprovalPersonnel) => {
    selectedIds.forEach((rowId) => {
      const team = teamById[rowId];
      if (!team) return;
      revertTeamApproval({
        teamId: team.teamId,
        ...(type === ApprovalPersonnel.Developer ? { developer: 'true' } : { support: 'true' }),
        tenant: team.tenant,
        realmId: team.realmId,
      });
    });
  };

  const onPageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
      setSelectedIdsState(new Set());
      handlePageChange({ newPageNumber: newPage });
      const mergedParams = Object.fromEntries(queryParams.entries());
      mergedParams.page = String(newPage);
      pushQueryParams(mergedParams);
    },
    [handlePageChange, pushQueryParams, queryParams],
  );

  const onSort = useCallback(
    (columnId: string) => {
      if (orderBy === columnId) {
        const nextDir =
          orderDirection === ORDER_DIRECTION.ASC ? ORDER_DIRECTION.DESC : ORDER_DIRECTION.ASC;
        setOrderDirection(nextDir);
        setSelectedIdsState(new Set());
      } else {
        const nextDir = ORDER_DIRECTION.DESC;
        setOrderBy(columnId);
        setOrderDirection(nextDir);
        setSelectedIdsState(new Set());
      }
    },
    [orderBy, orderDirection],
  );

  const paginationProps = useMemo(
    () => ({
      count: total,
      page: pageNumber,
      rowsPerPage: limit,
      rowsPerPageOptions: [],
      onPageChange,
      indexBase: 1 as const,
    }),
    [limit, onPageChange, pageNumber, total],
  );

  return (
    <Box
      sx={{
        p: 4,
      }}
    >
      <StandardPageHeader
        title="Data Deletion Review"
        subtitle="Review and process data deletion requests"
        icon="Teams"
        sx={{ mb: 6 }}
      />

      <DataDeletionReviewTable
        teams={teams}
        isLoading={isLoading || isUpdatingTeamApprover || isRevertingTeamApproval}
        selectedIds={selectedIds}
        hasSelectionOnPage={hasSelectionOnPage}
        allOnPageSelected={isAllSelectedOnPage}
        isIndeterminateOnPage={isIndeterminateOnPage}
        onToggleRow={toggleRow}
        onToggleSelectAll={toggleSelectAllOnPage}
        paginationProps={paginationProps}
        onBulkApprove={handleBulkApprove}
        onBulkUnapprove={handleBulkUnapprove}
        orderBy={orderBy}
        orderDirection={orderDirection}
        onSort={onSort}
      />
    </Box>
  );
};

export default DataDeletionReviewPage;
